/**
 * PATH: apps/quote/src/components/builder/scope-sheet-grid/visible-columns-helper.ts
 * FILE_ID: QB-SCOPESHEET-VISIBLE-001
 * PURPOSE: Single source of truth for "which optional columns are effectively
 *          visible on a given quote" — distinguishing the three states an
 *          estimator's quote.config can be in.
 * WHY: Estimators repeatedly asked for an Excel-grade grid where every
 *      input column is on by default. The previous behaviour treated a
 *      brand-new quote (config.visible_columns absent entirely) the same
 *      as a quote where the estimator had explicitly hidden every column
 *      (config.visible_columns === []). That made fresh quotes feel
 *      barebones — Plant detail, Subcon detail, per-line cost columns,
 *      and notes were all hidden until the user opened Setup → Visible
 *      columns.
 *
 *      We split the semantics:
 *        - visible_columns absent       → "use the estimator default"
 *                                         which is ALL optional ids
 *                                         (Excel-grade out of the box).
 *        - visible_columns === []       → "I explicitly hid all of them"
 *                                         (preserved as-is so the user's
 *                                         deliberate choice isn't undone).
 *        - visible_columns: ['x','y']   → exactly those.
 *
 *      The grid renderer + Setup picker + inline header menu must all
 *      use the SAME effective list, otherwise the toggle state in Setup
 *      drifts away from what the grid is actually showing. That's why
 *      this helper exists rather than open-coding the fallback in three
 *      places.
 *
 * FLOW:
 *   ┌────────────────────┐    ┌─────────────────────────┐    ┌──────────────────┐
 *   │ quote.config       │ -> │ getEffectiveVisible     │ -> │ Grid renders     │
 *   │ .visible_columns   │    │ ColumnIds(quote)        │    │ optional cols    │
 *   └────────────────────┘    └─────────────────────────┘    └──────────────────┘
 *
 * DEPENDENCIES:
 *   - BASE_COLUMN_BANDS from scope-sheet-columns.ts — declares which
 *     columns carry the `optional: true` flag.
 *
 * RELATED:
 *   - QB-SCOPESHEET-COLUMNS-001 (scope-sheet-columns.ts)
 *   - QB-SETUP-VISIBLECOLS-001 (VisibleColumnsSection.tsx)
 */

import { BASE_COLUMN_BANDS } from './scope-sheet-columns'

/**
 * Read the raw `visible_columns` array off a quote, or `undefined` if the
 * key isn't set at all. Empty array (user explicitly hid all) and a
 * non-empty array are returned as-is. A non-array value is treated as
 * "key not set" — defensive but matches the prior `Array.isArray` guard.
 */
export function readRawVisibleColumns(
  quote: { config?: unknown } | null | undefined,
): string[] | undefined {
  if (!quote) return undefined
  const cfg = (quote.config ?? {}) as Record<string, unknown>
  if (!('visible_columns' in cfg)) return undefined
  const raw = cfg.visible_columns
  if (!Array.isArray(raw)) return undefined
  return raw.filter((x): x is string => typeof x === 'string')
}

/**
 * The full set of optional column ids declared by the column-band schema.
 * Computed once at module load — `BASE_COLUMN_BANDS` is a static const so
 * this is safe to memoise this way.
 */
export const ALL_OPTIONAL_COLUMN_IDS: readonly string[] = (() => {
  const out: string[] = []
  for (const band of BASE_COLUMN_BANDS) {
    for (const col of band.columns) {
      if (col.optional) out.push(col.id)
    }
  }
  return Object.freeze(out)
})()

/**
 * The effective list of visible optional column ids for a quote.
 *
 *   Input                                Output
 *   ─────────────────────────────────    ────────────────────────────
 *   { config: undefined }                ALL_OPTIONAL_COLUMN_IDS
 *   { config: {} }                       ALL_OPTIONAL_COLUMN_IDS
 *   { config: { visible_columns: [] } }  []   (explicit hide-all)
 *   { config: { visible_columns: [a] } } [a]
 *
 * The "explicit hide-all" case is preserved deliberately: an estimator
 * who hides every advanced column should not be force-shown them again
 * the next time they open the quote.
 */
export function getEffectiveVisibleColumnIds(
  quote: { config?: unknown } | null | undefined,
): string[] {
  const raw = readRawVisibleColumns(quote)
  if (raw === undefined) return [...ALL_OPTIONAL_COLUMN_IDS]
  return raw
}
